import React, { FC, useCallback, useMemo } from 'react';
import { DocsNodeType } from 'src/models/documentsLibrary/types';
import styles from './index.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-light-svg-icons';
import {
  useDeleteFolder,
  useDeleteFile,
  useCurrentFolderContext,
  getUpdateCacheOnDeleteFolder,
  getUpdateCacheOnDeleteFile
} from 'src/models/documentsLibrary/hooks';
import Tooltip from 'src/common/Tooltip';
import notification from 'src/common/notification';
import confirm from 'src/common/confirm';
import Expand from 'src/common/Expand';
import { useTranslation } from 'react-i18next';
import { IDocumentsTableProps, IFileTask } from '../../types';
import modalStyles from 'src/common/Modal/index.module.scss';
import { Button } from 'src/common';

interface ITasksNamesProps {
  attachedTo?: IFileTask[];
}

const TasksNames: FC<ITasksNamesProps> = props => {
  const { attachedTo } = props;
  const { t } = useTranslation();

  return (
    <>
      <span className={styles.tasksTitle}>{`${t('Common.attachedTo')}:`}</span>
      <Expand visibility={100}>
        <ol className={styles.tasksList}>
          {attachedTo &&
            attachedTo.map(task => (
              <li className={styles.tasksItem} key={task.id}>
                <span className={styles.lotsRangeNumber}>{task.name}</span>
              </li>
            ))}
        </ol>
      </Expand>
    </>
  );
};

interface IProps {
  docsNode: IDocumentsTableProps;
}

export const DocumentsDelete: FC<IProps> = props => {
  const { docsNode } = props;
  const { id, childNodes, nodeType, attachedTo } = docsNode;
  const { t } = useTranslation();

  const currentFolder = useCurrentFolderContext();
  const [deleteFolder] = useDeleteFolder();
  const [deleteFile] = useDeleteFile();

  const isDisabledFolder = useMemo(() => {
    return !!childNodes?.length;
  }, [childNodes]);
  const isDisabledFile = useMemo(() => {
    return !!attachedTo?.length;
  }, [attachedTo]);

  const isFolder = useMemo(() => {
    return nodeType === DocsNodeType.FOLDER;
  }, [nodeType]);

  const onDeleteFolder = useCallback(async () => {
    confirm.confirm({
      title: t('DocumentLibrary.deleteFolder'),
      content: t('DocumentLibrary.deleteFolderConfirm'),
      okText: t('Common.delete'),
      cancelText: t('Common.cancel'),
      className: modalStyles.confirmModal,
      centered: true,
      onOk() {
        deleteFolder({
          variables: { id },
          update: getUpdateCacheOnDeleteFolder(id, currentFolder.id),
          optimisticResponse: {
            __typename: 'Mutation',
            rmFolder: true
          }
        }).catch(() => {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        });
      }
    });
  }, [currentFolder.id, deleteFolder, id, t]);

  const onDeleteFile = useCallback(async () => {
    confirm.confirm({
      title: t('DocumentLibrary.deleteFile'),
      content: t('DocumentLibrary.deleteFileConfirm'),
      okText: t('Common.delete'),
      cancelText: t('Common.cancel'),
      className: modalStyles.confirmModal,
      centered: true,
      onOk() {
        deleteFile({
          variables: { id },
          update: getUpdateCacheOnDeleteFile(id, currentFolder.id),
          optimisticResponse: {
            __typename: 'Mutation',
            rmFile: true
          }
        }).catch(() => {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        });
      }
    });
  }, [currentFolder.id, deleteFile, id, t]);

  return isFolder ? (
    <>
      <Tooltip title={isDisabledFolder ? t('DocumentLibrary.deleteFolderWarning') : undefined}>
        <div className={styles.iconContainer}>
          <Button
            type={'text'}
            onClick={onDeleteFolder}
            icon={<FontAwesomeIcon icon={faTrash} />}
            disabled={isDisabledFolder}
          />
        </div>
      </Tooltip>
    </>
  ) : (
    <>
      <Tooltip
        title={isDisabledFile ? <TasksNames attachedTo={attachedTo} /> : undefined}
        overlayClassName={styles.tooltip}
      >
        <div className={styles.iconContainer}>
          <Button
            disabled={isDisabledFile}
            type={'text'}
            onClick={onDeleteFile}
            icon={<FontAwesomeIcon icon={faTrash} />}
          />
        </div>
      </Tooltip>
    </>
  );
};

export default DocumentsDelete;
